import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';

import { gql } from '../../graphql/client';
import { M_DELETE_ASSET, Q_LIST_ASSETS } from '../../graphql/operations';
import type { Asset } from '../../types/forge';
import { uploadAsset } from './uploadAsset';

/**
 * Reusable asset picker — a modal that lists existing assets of one or
 * more `kinds`, lets the user upload a new one, and on selection passes
 * the asset's storage_url back to the caller.
 *
 * Org-scoped via Hasura RLS; the picker itself doesn't need to know
 * which app it's editing.
 *
 * The picker writes nothing to the consumer's data on its own — it
 * only invokes `onPick(url, asset)` when the user clicks an asset. The
 * caller is responsible for persisting that URL into whatever field
 * triggered the picker (FrontendApp.icon_asset_url, viewSource.value,
 * etc.).
 */
export interface AssetPickerProps {
  open: boolean;
  onClose: () => void;
  onPick: (storageUrl: string, asset: Asset) => void;
  /** Filter the listing + tag uploads with this kind. */
  kind: 'icon' | 'splash' | 'image';
  /** Title shown in the modal header. Defaults to "Select <kind>". */
  title?: string;
}

export function AssetPicker({ open, onClose, onPick, kind, title }: AssetPickerProps) {
  const queryClient = useQueryClient();
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [uploadError, setUploadError] = useState<string | null>(null);

  // We always also show 'image' assets so authors can reuse a generic
  // image as an icon if they want. The kind on upload still carries the
  // explicit kind so the type filter is meaningful for the next picker.
  const kinds = kind === 'image' ? ['image'] : [kind, 'image'];

  const list = useQuery({
    queryKey: ['assets', kinds.join(',')],
    enabled: open,
    queryFn: () =>
      gql<{ Asset: Asset[] }>(Q_LIST_ASSETS, { kinds }),
  });

  const upload = useMutation({
    mutationFn: async (file: File) => uploadAsset({ file, kind }),
    onSuccess() {
      setUploadError(null);
      queryClient.invalidateQueries({ queryKey: ['assets'] });
    },
    onError(err: Error) {
      setUploadError(err.message);
    },
  });

  const remove = useMutation({
    mutationFn: (id: string) => gql(M_DELETE_ASSET, { id }),
    onSuccess() {
      queryClient.invalidateQueries({ queryKey: ['assets'] });
    },
  });

  useEffect(() => {
    if (!open) setUploadError(null);
  }, [open]);

  if (!open) return null;

  const items = list.data?.Asset ?? [];

  return (
    <div style={s.overlay} onClick={onClose}>
      <div style={s.modal} onClick={(e) => e.stopPropagation()}>
        <header style={s.header}>
          <h2 style={s.title}>{title ?? `Select ${kind}`}</h2>
          <button type="button" style={s.closeBtn} onClick={onClose} aria-label="Close">
            ×
          </button>
        </header>

        <div style={s.uploadRow}>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            style={{ display: 'none' }}
            onChange={(e) => {
              const f = e.target.files?.[0];
              if (f) upload.mutate(f);
              if (fileInputRef.current) fileInputRef.current.value = '';
            }}
          />
          <button
            type="button"
            style={s.uploadBtn}
            disabled={upload.isPending}
            onClick={() => fileInputRef.current?.click()}
          >
            {upload.isPending ? 'Uploading…' : '+ Upload'}
          </button>
          {uploadError && <span style={s.error}>{uploadError}</span>}
        </div>

        <div style={s.gridContainer}>
          {list.isLoading && <p style={s.empty}>Loading…</p>}
          {list.error && (
            <p style={s.error}>
              Failed to load: {(list.error as Error).message}
            </p>
          )}
          {!list.isLoading && items.length === 0 && (
            <p style={s.empty}>No assets yet. Upload one to get started.</p>
          )}
          {items.length > 0 && (
            <ul style={s.grid}>
              {items.map((a) => (
                <li key={a.id} style={s.card}>
                  <button
                    type="button"
                    style={s.cardPick}
                    onClick={() => {
                      onPick(a.storage_url, a);
                      onClose();
                    }}
                  >
                    <img
                      src={a.storage_url}
                      alt={a.name}
                      style={s.thumb}
                      onError={(e) => {
                        (e.currentTarget as HTMLImageElement).style.opacity = '0.2';
                      }}
                    />
                    <span style={s.cardName} title={a.name}>
                      {a.name}
                    </span>
                    <span style={s.cardKind}>{a.kind}</span>
                  </button>
                  <button
                    type="button"
                    style={s.cardDelete}
                    title="Delete asset"
                    onClick={() => {
                      if (window.confirm(`Delete "${a.name}"?`)) {
                        remove.mutate(a.id);
                      }
                    }}
                  >
                    ×
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}

const s: Record<string, React.CSSProperties> = {
  overlay: {
    position: 'fixed',
    inset: 0,
    backgroundColor: 'rgba(15, 23, 42, 0.5)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 100,
  },
  modal: {
    width: 720,
    maxWidth: '90vw',
    maxHeight: '85vh',
    backgroundColor: '#fff',
    borderRadius: 10,
    boxShadow: '0 20px 60px rgba(0,0,0,0.2)',
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '14px 18px',
    borderBottom: '1px solid #E2E8F0',
  },
  title: { margin: 0, fontSize: 14, fontWeight: 700, color: '#0F172A' },
  closeBtn: {
    width: 28,
    height: 28,
    fontSize: 18,
    border: 'none',
    backgroundColor: 'transparent',
    color: '#64748B',
    cursor: 'pointer',
  },
  uploadRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    padding: '12px 18px',
    borderBottom: '1px solid #F1F5F9',
  },
  uploadBtn: {
    padding: '8px 14px',
    fontSize: 13,
    fontWeight: 600,
    color: '#fff',
    backgroundColor: '#4F46E5',
    border: 'none',
    borderRadius: 6,
    cursor: 'pointer',
  },
  gridContainer: { padding: 18, overflow: 'auto' },
  grid: {
    listStyle: 'none',
    padding: 0,
    margin: 0,
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))',
    gap: 12,
  },
  card: {
    position: 'relative',
    border: '1px solid #E2E8F0',
    borderRadius: 8,
    overflow: 'hidden',
    backgroundColor: '#F8FAFC',
  },
  cardPick: {
    width: '100%',
    height: '100%',
    border: 'none',
    background: 'transparent',
    padding: 8,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: 6,
    cursor: 'pointer',
  },
  thumb: {
    width: 110,
    height: 110,
    objectFit: 'cover',
    borderRadius: 6,
    backgroundColor: '#fff',
  },
  cardName: {
    fontSize: 11,
    color: '#0F172A',
    width: '100%',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    textAlign: 'center',
  },
  cardKind: {
    fontSize: 10,
    color: '#94A3B8',
    textTransform: 'uppercase',
    letterSpacing: 0.4,
  },
  cardDelete: {
    position: 'absolute',
    top: 4,
    right: 4,
    width: 22,
    height: 22,
    fontSize: 14,
    border: 'none',
    backgroundColor: 'rgba(15,23,42,0.6)',
    color: '#fff',
    borderRadius: '50%',
    cursor: 'pointer',
    lineHeight: 1,
  },
  empty: { fontSize: 13, color: '#94A3B8', textAlign: 'center' as const, padding: 24 },
  error: { fontSize: 12, color: '#c00' },
};
